import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { APP_VERSION } from '@/config/version';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  RefreshCw, 
  ArrowRight, 
  Clock, 
  UserCheck, 
  LogIn, 
  Calendar, 
  Download, 
  FileEdit, 
  Receipt,
  Car,
  Trophy,
  UserPlus,
  Mail,
  CreditCard,
  TrendingUp,
  Activity,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  XCircle,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, active: 0, resigned: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [loginStats, setLoginStats] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [membershipRequests, setMembershipRequests] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [vehicleCount, setVehicleCount] = useState(0);

  const getToken = () => localStorage.getItem('shrt_token');

  const fetchAllData = async () => {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Fetch member stats
      const membersResponse = await axios.get(`${API_URL}/api/members?limit=1`, { headers });
      
      // Fetch resigned members count
      let resignedCount = 0;
      try {
        const resignedResponse = await axios.get(`${API_URL}/api/admin/members/resigned`, { headers });
        resignedCount = resignedResponse.data.count || 0;
      } catch (e) {}
      
      setStats({ 
        total: membersResponse.data.total, 
        active: membersResponse.data.total - resignedCount,
        resigned: resignedCount
      });

      // Fetch login stats
      try {
        const loginResponse = await axios.get(`${API_URL}/api/admin/login-stats`, { headers });
        setLoginStats(loginResponse.data);
      } catch (e) {}

      // Fetch pending changes
      try {
        const changesResponse = await axios.get(`${API_URL}/api/admin/pending-changes`, { headers });
        setPendingChanges(changesResponse.data.changes?.length || 0);
      } catch (e) {}

      // Fetch invoice stats
      try {
        const year = new Date().getFullYear();
        const invoiceResponse = await axios.get(`${API_URL}/api/admin/invoices/report/summary?year=${year}`, { headers });
        setInvoiceStats(invoiceResponse.data);
      } catch (e) {}

      // Fetch membership requests
      try {
        const requestsResponse = await axios.get(`${API_URL}/api/admin/membership-requests`, { headers });
        const pending = requestsResponse.data.requests?.filter(r => r.status === 'pending') || [];
        setMembershipRequests(pending.length);
      } catch (e) {}

      // Fetch upcoming events
      try {
        const eventsResponse = await axios.get(`${API_URL}/api/events?upcoming=true&limit=3`, { headers });
        setUpcomingEvents(eventsResponse.data.events || []);
      } catch (e) {}

      // Fetch vehicle count
      try {
        const vehiclesResponse = await axios.get(`${API_URL}/api/public-vehicles`, { headers });
        setVehicleCount(vehiclesResponse.data.vehicles?.length || 0);
      } catch (e) {}

    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/sync/monday`);
      toast.success(`Sync erfolgreich: ${response.data.synced_count} aktualisiert, ${response.data.new_members} neue`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleExportCSV = async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/export/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shrt_members.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('CSV exportiert');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Quick action cards for admin
  const quickActions = [
    { 
      label: 'Mitglieder', 
      icon: Users, 
      path: '/members', 
      color: 'bg-blue-500/10 text-blue-500',
      count: stats.total 
    },
    { 
      label: 'Rechnungen', 
      icon: Receipt, 
      path: '/admin/invoices', 
      color: 'bg-green-500/10 text-green-500',
      count: invoiceStats?.open_invoices,
      suffix: 'offen'
    },
    { 
      label: 'Anträge', 
      icon: UserPlus, 
      path: '/admin/membership-requests', 
      color: 'bg-yellow-500/10 text-yellow-500',
      count: membershipRequests,
      highlight: membershipRequests > 0
    },
    { 
      label: 'Änderungen', 
      icon: FileEdit, 
      path: '/admin/changes', 
      color: 'bg-orange-500/10 text-orange-500',
      count: pendingChanges,
      highlight: pendingChanges > 0
    },
    { 
      label: 'Check-in', 
      icon: QrCode, 
      path: '/admin/checkin', 
      color: 'bg-purple-500/10 text-purple-500'
    },
  ];

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-['Oswald'] text-2xl sm:text-3xl font-bold text-foreground uppercase tracking-tight">
                Dashboard
              </h1>
              <Link to="/about" className="text-xs px-2 py-0.5 bg-primary/20 text-primary font-mono hover:bg-primary/30 transition-colors">
                v{APP_VERSION}
              </Link>
            </div>
            <p className="text-muted-foreground text-sm">
              Willkommen, {user?.name?.split(' ')[0] || 'Admin'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleExportCSV}
              variant="outline"
              size="sm"
              className="rounded-none text-xs"
            >
              <Download size={14} className="mr-1" />
              <span className="hidden sm:inline">CSV</span>
            </Button>
            <Button
              onClick={handleSync}
              disabled={syncing}
              size="sm"
              className="rounded-none text-xs"
            >
              <RefreshCw size={14} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
              Sync
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {(pendingChanges > 0 || membershipRequests > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {membershipRequests > 0 && (
              <Link 
                to="/admin/membership-requests"
                className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20 transition-colors"
              >
                <UserPlus className="text-yellow-500 flex-shrink-0" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{membershipRequests} neue Anträge</p>
                </div>
                <ChevronRight className="text-yellow-500 flex-shrink-0" size={16} />
              </Link>
            )}
            {pendingChanges > 0 && (
              <Link 
                to="/admin/changes"
                className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 transition-colors"
              >
                <FileEdit className="text-orange-500 flex-shrink-0" size={20} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{pendingChanges} Änderungen warten</p>
                </div>
                <ChevronRight className="text-orange-500 flex-shrink-0" size={16} />
              </Link>
            )}
          </div>
        )}

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map((action) => (
            <Link
              key={action.path}
              to={action.path}
              className={`p-3 sm:p-4 bg-card border hover:border-primary/50 transition-colors ${
                action.highlight ? 'border-primary/50 animate-pulse' : 'border-border'
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-2 rounded-lg ${action.color}`}>
                  <action.icon size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-foreground">
                    {loading ? '-' : action.count ?? '-'}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider truncate">
                    {action.label} {action.suffix && <span className="hidden sm:inline">({action.suffix})</span>}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Main Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Members */}
          <div className="bg-card border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase">Mitglieder</span>
              <Users size={16} className="text-primary" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{loading ? '-' : stats.total}</p>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500">{stats.active} aktiv</span>
              {stats.resigned > 0 && <span> · {stats.resigned} ausgetreten</span>}
            </p>
          </div>

          {/* Invoices */}
          {invoiceStats && (
            <div className="bg-card border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground uppercase">Rechnungen {new Date().getFullYear()}</span>
                <Receipt size={16} className="text-green-500" />
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{invoiceStats.collection_rate?.toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">
                <span className="text-green-500">{invoiceStats.paid_invoices} bezahlt</span>
                <span> · {invoiceStats.open_invoices} offen</span>
              </p>
            </div>
          )}

          {/* Logins Today */}
          {loginStats && (
            <div className="bg-card border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground uppercase">Logins heute</span>
                <LogIn size={16} className="text-blue-500" />
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{loginStats.today_unique_users}</p>
              <p className="text-xs text-muted-foreground">
                {loginStats.total_logins} gesamt
              </p>
            </div>
          )}

          {/* Vehicles */}
          <div className="bg-card border border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase">Fahrzeuge</span>
              <Car size={16} className="text-purple-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold">{loading ? '-' : vehicleCount}</p>
            <p className="text-xs text-muted-foreground">
              in der Galerie
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Logins */}
          {loginStats?.recent_logins?.length > 0 && (
            <div className="bg-card border border-border">
              <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider">
                  Letzte Logins
                </h2>
                <Activity size={16} className="text-muted-foreground" />
              </div>
              <div className="divide-y divide-border max-h-[280px] overflow-y-auto">
                {loginStats.recent_logins.slice(0, 8).map((login, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                        {login.member_name?.charAt(0) || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate text-xs sm:text-sm">{login.member_name}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-muted-foreground text-[10px] sm:text-xs">
                        {new Date(login.timestamp).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Events */}
          <div className="bg-card border border-border">
            <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider">
                Nächste Events
              </h2>
              <Link to="/events" className="text-xs text-primary hover:underline">Alle</Link>
            </div>
            {upcomingEvents.length > 0 ? (
              <div className="divide-y divide-border">
                {upcomingEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-10 h-10 bg-primary/10 flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary">
                        {new Date(event.date).getDate()}
                      </span>
                      <span className="text-[9px] text-primary uppercase">
                        {new Date(event.date).toLocaleDateString('de-CH', { month: 'short' })}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground text-sm truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{event.location || 'Ort TBD'}</p>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground text-sm">
                <Calendar className="mx-auto mb-2 opacity-50" size={24} />
                <p>Keine anstehenden Events</p>
              </div>
            )}
          </div>
        </div>

        {/* Invoice Summary */}
        {invoiceStats && (
          <div className="bg-card border border-border">
            <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-['Oswald'] text-sm sm:text-base font-bold uppercase tracking-wider">
                Finanzen {new Date().getFullYear()}
              </h2>
              <Link to="/admin/invoices" className="text-xs text-primary hover:underline">Details</Link>
            </div>
            <div className="p-4">
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Einzugsquote</span>
                  <span className="font-bold text-foreground">{invoiceStats.collection_rate?.toFixed(0)}%</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                    style={{ width: `${invoiceStats.collection_rate || 0}%` }}
                  />
                </div>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-muted/30">
                  <p className="text-lg sm:text-xl font-bold text-foreground">{invoiceStats.total_invoices}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Total</p>
                </div>
                <div className="p-2 bg-green-500/10">
                  <p className="text-lg sm:text-xl font-bold text-green-500">{formatCurrency(invoiceStats.paid_amount)}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Bezahlt</p>
                </div>
                <div className="p-2 bg-yellow-500/10">
                  <p className="text-lg sm:text-xl font-bold text-yellow-500">{formatCurrency(invoiceStats.open_amount)}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Offen</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Links Footer */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link
            to="/admin/newsletter"
            className="flex items-center gap-2 p-3 bg-card border border-border hover:border-primary/50 transition-colors text-sm"
          >
            <Mail size={16} className="text-primary" />
            <span className="truncate">Newsletter</span>
          </Link>
          <Link
            to="/car-of-the-month"
            className="flex items-center gap-2 p-3 bg-card border border-border hover:border-primary/50 transition-colors text-sm"
          >
            <Trophy size={16} className="text-yellow-500" />
            <span className="truncate">Auto des Monats</span>
          </Link>
          <Link
            to="/admin/agb"
            className="flex items-center gap-2 p-3 bg-card border border-border hover:border-primary/50 transition-colors text-sm"
          >
            <FileEdit size={16} className="text-muted-foreground" />
            <span className="truncate">AGB</span>
          </Link>
          <Link
            to="/admin/status"
            className="flex items-center gap-2 p-3 bg-card border border-border hover:border-primary/50 transition-colors text-sm"
          >
            <Activity size={16} className="text-green-500" />
            <span className="truncate">System</span>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
